import { HTML_LINK_TARGET } from '../../../../../constants/html'
import {
  useInitData,
  useLinkLikeClick,
  useNotification,
} from '../../../../../hooks'
import { stopPropagation } from '../../../../../utils/events'
import { getRequestProfilePathname } from '../../../../../utils/link'
import {
  EmailAddNoteActionModal,
  getEmailAddNoteInitialValues,
} from '../../../../Email'
import {
  AttachServiceRequestsModal,
  getRequestThreadPrintPageLink,
} from '../../../../Request'
import { AttachTasksModal } from '../../../../Task'
import { useRequestProfileContext } from '../../../RequestProfile.hooks'
import {
  deleteRequestThread,
  moveRequestThreadToRequest,
  toggleRequestThreadRead,
  updateRequestThread,
} from '../../RequestProfileTabEmails.api'
import {
  REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION,
  REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY,
} from '../../RequestProfileTabEmails.constants'
import { RequestProfileTabEmailsMoveToTabModal } from '../../RequestProfileTabEmailsMoveToTabModal'

import {
  getRequestProfileTabEmailsItemActionsErrorTitleKey,
  getRequestProfileTabEmailsItemActionsNotificationTitleKey,
} from './useRequestProfileTabEmailsItemActions.utils'

export function useRequestProfileTabEmailsItemActions({
  requestId,
  setPerformingAction,
  showDeleteConfirmModal,
  onCloseModal,
  onReload,
  onSetModal,
}) {
  const { outboundUuid, requestData } = useRequestProfileContext()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { handleClick: handleLinkClick } = useLinkLikeClick()

  const { availableSections } = useInitData()

  function handleOpenInNewTab(uuid) {
    handleLinkClick({
      link: getRequestProfilePathname({
        additionalParams: `?${REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY}=${uuid}`,
        availableSections,
        id: requestId,
      }),
      target: HTML_LINK_TARGET.blank,
    })
  }

  function handlePrint(uuid) {
    handleLinkClick({
      link: getRequestThreadPrintPageLink({
        id: uuid,
        outboundUuid,
        requestId,
      }),
      target: HTML_LINK_TARGET.blank,
    })
  }

  function handleSuccessAction(action) {
    onCloseModal()
    handleAddSuccessNotification(
      getRequestProfileTabEmailsItemActionsNotificationTitleKey(action)
    )

    setPerformingAction(true)

    return onReload().finally(() => {
      setPerformingAction(false)
    })
  }

  function handleErrorAction(action) {
    onCloseModal()
    handleAddErrorNotification(
      getRequestProfileTabEmailsItemActionsErrorTitleKey(action)
    )
  }

  function handleUpdateRequestThread({ action, data, uuid }) {
    return updateRequestThread({ data, requestId, uuid })
      .then(() => {
        handleSuccessAction(action)
      })
      .catch(() => {
        handleErrorAction(action)
      })
  }

  function setImportant(uuid, important) {
    return handleUpdateRequestThread({
      action: important
        ? REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsStarred
        : REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsUnstarred,
      data: { starred: important },
      uuid,
    })
  }

  function handleToggleRead({ read, uuid }) {
    const action = read
      ? REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsRead
      : REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsUnread

    return toggleRequestThreadRead({ outboundUuid, read, requestId, uuid })
      .then(() => {
        handleSuccessAction(action)
      })
      .catch(() => {
        handleErrorAction(action)
      })
  }

  function handleAddNote(uuid, values) {
    return handleUpdateRequestThread({
      action: REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.AddNote,
      data: { notes: values.notes || null },
      uuid,
    })
  }

  function handleMoveToTab({ audience, uuid }) {
    return handleUpdateRequestThread({
      action: REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MoveToTab,
      data: { audience },
      uuid,
    })
  }

  function handleMoveToServiceRequest({ newRequestId, uuid }) {
    return moveRequestThreadToRequest({ newRequestId, requestId, uuid })
      .then(() => {
        handleSuccessAction(
          REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MoveToAnotherServiceRequest
        )
      })
      .catch(() => {
        handleErrorAction(
          REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MoveToAnotherServiceRequest
        )
      })
  }

  function handleAttachToActivity({ activityIds, uuid }) {
    return handleUpdateRequestThread({
      action: REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.AttachToActivity,
      data: { activities: activityIds },
      uuid,
    })
  }

  function handleDelete(uuid) {
    return deleteRequestThread({ requestId, uuid })
      .then(() => {
        handleSuccessAction(REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.Delete)
      })
      .catch(() => {
        handleErrorAction(REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.Delete)
      })
  }

  function setItemAction(action, { activities, audience, notes, uuid }) {
    function handleSubmitDelete(e) {
      stopPropagation(e)
      onCloseModal()

      return handleDelete(uuid)
    }

    switch (action) {
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.OpenInNewTab:
        handleOpenInNewTab(uuid)
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsRead:
        handleToggleRead({ read: true, uuid })
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsUnread:
        handleToggleRead({ read: false, uuid })
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsStarred:
        setImportant(uuid, true)
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MarkAsUnstarred:
        setImportant(uuid, false)
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.Print:
        handlePrint(uuid)
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MoveToTab:
        onSetModal(
          <RequestProfileTabEmailsMoveToTabModal
            currentAudience={audience}
            onClose={onCloseModal}
            onSubmit={nextAudience =>
              handleMoveToTab({ audience: nextAudience, uuid })
            }
          />
        )
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.MoveToAnotherServiceRequest:
        onSetModal(
          <AttachServiceRequestsModal
            actionsProps={{
              buttonAttachProps: {
                text: 'Modal.TabEmailsMoveToServiceRequestSubmitKey',
              },
            }}
            counterProps={{
              text: 'Modal.TabEmailsServiceRequestsCounterKey',
            }}
            excludeIds={[requestId]}
            headerProps={{
              text: 'Modal.TabEmailsMoveToServiceRequestTitleKey',
            }}
            onAttach={({ id }) =>
              handleMoveToServiceRequest({ newRequestId: id, uuid })
            }
            onClose={onCloseModal}
          />
        )
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.AttachToActivity:
        onSetModal(
          <AttachTasksModal
            counterProps={{
              text: 'Modal.TabEmailsActivitiesCounterKey',
            }}
            headerProps={{
              text: 'Modal.RequestProfileTabEmailsAttachToActivityTitleKey',
            }}
            initialSelection={activities}
            pipelineId={requestData?.pipelineId}
            onAttach={activityIds =>
              handleAttachToActivity({ activityIds, uuid })
            }
            onClose={onCloseModal}
          />
        )
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.AddNote:
        onSetModal(
          <EmailAddNoteActionModal
            id={uuid}
            initialValues={getEmailAddNoteInitialValues(notes)}
            onClose={onCloseModal}
            onSubmit={handleAddNote}
          />
        )
        break
      case REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTION.Delete:
        showDeleteConfirmModal({ onSubmit: handleSubmitDelete })
        break
      default:
        break
    }
  }

  return {
    setImportant,
    setItemAction,
  }
}
